import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Wallet, CreditCard, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  loadCompanyPaymentAccounts,
  upsertCompanyPaymentAccounts,
  type CardAccount,
} from "@/lib/companyPaymentAccounts";

interface Props {
  companyId: string;
}

/**
 * Tab "Cuentas de pago" dentro del maestro de la empresa.
 *
 * Permite al administrador configurar las contrapartidas que se usarán al
 * generar el asiento de pago en SUENLACE para facturas pagadas al CONTADO
 * (caja) o con TARJETA. Se admiten varias tarjetas con etiqueta libre
 * (p. ej. "TJ 6029", "TJ Visa Empresa"…).
 */
const PaymentAccountsTab = ({ companyId }: Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cashAccount, setCashAccount] = useState("");
  const [cardAccounts, setCardAccounts] = useState<CardAccount[]>([]);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    loadCompanyPaymentAccounts(companyId).then((r) => {
      setCashAccount(r.cashAccount);
      setCardAccounts(r.cardAccounts.length > 0 ? r.cardAccounts : []);
      setLoading(false);
    });
  }, [companyId]);

  const updateCard = (index: number, patch: Partial<CardAccount>) => {
    setCardAccounts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );
  };

  const addCard = () => {
    setCardAccounts((prev) => [...prev, { label: "", account: "" }]);
  };

  const removeCard = (index: number) => {
    setCardAccounts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await upsertCompanyPaymentAccounts(
      companyId,
      cashAccount,
      cardAccounts,
    );
    setSaving(false);
    if (error) {
      toast({ title: "Error al guardar", description: error, variant: "destructive" });
    } else {
      toast({ title: "Cuentas de pago guardadas" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-6">
      <div className="rounded-md border bg-muted/30 p-4 text-xs text-muted-foreground">
        Estas cuentas se usan al generar el <strong>asiento de pago</strong> en
        SUENLACE para las facturas pagadas al <strong>CONTADO</strong> o con
        <strong> TARJETA</strong>. Puedes añadir varias tarjetas (caja, Visa,
        Mastercard, una por terminal…). Si la forma de pago en la factura
        contiene dígitos (p. ej. <code>TJ6029</code>), se sugerirá la tarjeta
        cuya etiqueta o cuenta contenga esos mismos dígitos.
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Wallet className="h-4 w-4" />
          Cuenta de caja (CONTADO / EFECTIVO)
        </Label>
        <Input
          value={cashAccount}
          onChange={(e) => setCashAccount(e.target.value)}
          placeholder="5700000000"
          className="font-mono"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <CreditCard className="h-4 w-4" />
            Cuentas de tarjeta
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCard}
            className="h-7 gap-1 text-xs"
          >
            <Plus className="h-3 w-3" /> Añadir tarjeta
          </Button>
        </div>

        {cardAccounts.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            Aún no hay tarjetas configuradas. Añade al menos una para que se
            proponga automáticamente el asiento de pago.
          </p>
        )}

        <div className="space-y-2">
          {cardAccounts.map((card, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_1.2fr_auto] gap-2 items-center">
              <Input
                value={card.label}
                onChange={(e) => updateCard(idx, { label: e.target.value })}
                placeholder="Etiqueta (p. ej. TJ 6029)"
                className="h-8 text-xs"
              />
              <Input
                value={card.account}
                onChange={(e) => updateCard(idx, { account: e.target.value })}
                placeholder="5525000000"
                className="h-8 text-xs font-mono"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCard(idx)}
                className="h-8 w-8 text-destructive hover:text-destructive"
                title="Eliminar tarjeta"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2 bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar cuentas
        </Button>
      </div>
    </div>
  );
};

export default PaymentAccountsTab;
